import {
  CalendarCheck2,
  CalendarClock,
  CalendarPlus,
  CalendarX2,
  CircleHelp,
  Headset,
  Info,
  PhoneCall,
  Voicemail,
  type LucideIcon,
} from 'lucide-react'

import type { InteractionIntent } from '../conversations-contracts'
import type { HandoffPresentation } from '../handoff-presentation'
import styles from './IntentBadge.module.css'

export type IntentTone =
  'booked' | 'request' | 'reschedule' | 'cancellation' | 'followup' | 'question'

// The latest customer interaction's one intent, never the request history:
// open Action Requests stay with Needs action and the Open requests filter.
// Shared by the conversation row and the summary. Each category has its own
// tone and icon, so it never rests on color alone; staff follow-up (front desk,
// callback, message) shares one tone.
export const INTENT_PRESENTATION: Record<
  InteractionIntent,
  { label: string; tone: IntentTone; icon: LucideIcon }
> = {
  APPOINTMENT_BOOKED: {
    label: 'Appointment Booked',
    tone: 'booked',
    icon: CalendarCheck2,
  },
  APPOINTMENT_REQUEST: {
    label: 'Appointment Request',
    tone: 'request',
    icon: CalendarPlus,
  },
  RESCHEDULE_REQUEST: {
    label: 'Reschedule Request',
    tone: 'reschedule',
    icon: CalendarClock,
  },
  CANCELLATION_REQUEST: {
    label: 'Cancellation Request',
    tone: 'cancellation',
    icon: CalendarX2,
  },
  FRONT_DESK_REQUEST: {
    label: 'Front Desk Request',
    tone: 'followup',
    icon: Headset,
  },
  CALLBACK_REQUEST: {
    label: 'Callback Request',
    tone: 'followup',
    icon: PhoneCall,
  },
  MESSAGE_LEFT: { label: 'Message Left', tone: 'followup', icon: Voicemail },
  APPOINTMENT_QUESTION: {
    label: 'Appointment Question',
    tone: 'question',
    icon: CircleHelp,
  },
  GENERAL_INQUIRY: { label: 'General Inquiry', tone: 'question', icon: Info },
}

export function IntentBadge({
  intent,
  prominent = false,
}: {
  intent: InteractionIntent
  prominent?: boolean
}) {
  const { label, tone, icon: Icon } = INTENT_PRESENTATION[intent]
  return (
    <span
      className={`${styles.intentBadge} ${styles[tone]} ${prominent ? styles.prominent : ''}`}
      data-tone={tone}
    >
      <Icon size={prominent ? 13 : 11} strokeWidth={2.5} aria-hidden="true" />
      {label}
    </span>
  )
}

// What became of a handoff. It stays quieter than the intent it sits under: why
// the caller called leads, and this never replaces it. Each state has its own
// words and icon, so it never rests on color alone.
export function HandoffBadge({
  presentation: { tone, icon: Icon },
  label,
}: {
  presentation: Pick<HandoffPresentation, 'tone' | 'icon'>
  label: string
}) {
  return (
    <span
      className={`${styles.handoffBadge} ${styles[`handoff-${tone}`]}`}
      data-tone={tone}
    >
      <Icon size={11} strokeWidth={2.5} aria-hidden="true" />
      {label}
    </span>
  )
}
